import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  adminAgentService,
  type AdminAgentSession,
  type AdminSessionRequest,
} from '../../../../services/adminAgent.service';
import { DRAWER_DEFAULT_WIDTH, useDrawerWidth } from '../hooks/useDrawerWidth';

type SessionState = 'idle' | 'loading' | 'ready' | 'unavailable';

interface AdminAgentDrawerValue {
  open: boolean;
  width: number;
  /** True while the width is being dragged; the layout skips its transition then. */
  isResizing: boolean;
  sessionState: SessionState;
  session: AdminAgentSession | null;
  toggle: () => void;
  close: () => void;
  setWidth: (width: number) => void;
  resetWidth: () => void;
  setResizing: (resizing: boolean) => void;
  newConversation: () => void;
  openConversation: (conversationPublicId: string) => void;
}

const AdminAgentDrawerContext = createContext<AdminAgentDrawerValue | null>(null);

export const AdminAgentDrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [isResizing, setResizing] = useState(false);
  const [sessionState, setSessionState] = useState<SessionState>('idle');
  const [session, setSession] = useState<AdminAgentSession | null>(null);
  const sessionRef = useRef<AdminAgentSession | null>(null);
  const { width, setWidth, persist } = useDrawerWidth();

  const loadSession = useCallback(async (request: AdminSessionRequest) => {
    setSessionState('loading');
    try {
      const next = await adminAgentService.createSession(request);
      sessionRef.current = next;
      setSession(next);
      setSessionState('ready');
    } catch {
      if (sessionRef.current) {
        // Could not switch (e.g. conversation gone): stay on the current one.
        setSessionState('ready');
      } else {
        // Feature off or not an admin: the toggle disappears.
        setSessionState('unavailable');
        setOpen(false);
      }
    }
  }, []);

  const toggle = useCallback(() => {
    setOpen((current) => !current);
    if (sessionState === 'idle') void loadSession({});
  }, [loadSession, sessionState]);

  const value = useMemo<AdminAgentDrawerValue>(
    () => ({
      open,
      width,
      isResizing,
      sessionState,
      session,
      toggle,
      close: () => setOpen(false),
      setWidth,
      resetWidth: () => {
        setWidth(DRAWER_DEFAULT_WIDTH);
        persist(DRAWER_DEFAULT_WIDTH);
      },
      setResizing: (resizing) => {
        setResizing(resizing);
        if (!resizing) persist(width);
      },
      newConversation: () => void loadSession({ newConversation: true }),
      openConversation: (conversationPublicId) => void loadSession({ conversationPublicId }),
    }),
    [open, width, isResizing, sessionState, session, toggle, setWidth, persist, loadSession]
  );

  return (
    <AdminAgentDrawerContext.Provider value={value}>{children}</AdminAgentDrawerContext.Provider>
  );
};

/** `null` outside the provider (layouts that do not host the assistant). */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalAdminAgentDrawer(): AdminAgentDrawerValue | null {
  return useContext(AdminAgentDrawerContext);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAdminAgentDrawer(): AdminAgentDrawerValue {
  const value = useContext(AdminAgentDrawerContext);
  if (!value) throw new Error('useAdminAgentDrawer must be used inside AdminAgentDrawerProvider');
  return value;
}
